import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { Check, ArrowLeft, Loader2, Crown, Zap } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Build success/cancel URLs from window.location.origin so they always match
// whichever host the user is on (preview, production, custom domain).
export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [billing, setBilling] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: b }, { data: s }] = await Promise.all([
          api.get("/billing/config"),
          api.get("/billing/status"),
        ]);
        setBilling(b);
        setStatus(s);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, []);

  const startCheckout = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Checkout failed");
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.post("/billing/portal", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Portal unavailable");
      setLoading(false);
    }
  };

  const isPro = status?.tier === "pro";

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-12">
        <button
          onClick={() => nav(-1)}
          className="text-xs text-zinc-500 hover:text-zinc-200 flex items-center gap-1 font-mono mb-8"
          data-testid="pricing-back"
        >
          <ArrowLeft size={12} /> back
        </button>

        <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono mb-2">
          // upgrade
        </div>
        <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter mb-3">
          Practice without limits.
        </h1>
        <p className="text-zinc-400 max-w-xl mb-10">
          Free tier is great for kicking the tires. Pro unlocks the advanced multi-device labs,
          unlimited AI hints, and the full analytics suite — everything you need to actually pass
          the CCNA.
        </p>

        {err && (
          <div className="mb-6 p-3 border border-red-500/50 text-red-300 text-sm bg-red-500/10 font-mono" data-testid="pricing-error">
            {err}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Free tier */}
          <div className="bg-[#141416] border border-zinc-800 p-8 flex flex-col" data-testid="plan-free">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-3">free</div>
            <div className="font-display font-black text-4xl mb-1">$0</div>
            <div className="text-xs text-zinc-500 font-mono mb-6">forever</div>
            <ul className="space-y-2.5 text-sm text-zinc-300 mb-8 flex-1">
              <FreeLine ok>3 labs / month</FreeLine>
              <FreeLine ok>3 AI hints / day</FreeLine>
              <FreeLine ok>49 single-device labs</FreeLine>
              <FreeLine>Multi-device labs</FreeLine>
              <FreeLine>Full progress analytics</FreeLine>
              <FreeLine>Priority grading</FreeLine>
            </ul>
            {!isPro && (
              <div className="text-xs text-zinc-500 font-mono">
                you're on the free plan · {status?.labs_used ?? 0}/{status?.labs_limit ?? 3} labs this month
              </div>
            )}
          </div>

          {/* Pro tier */}
          <div className="bg-gradient-to-br from-[#00E5FF]/10 via-[#00E5FF]/5 to-transparent border-2 border-[#00E5FF]/60 p-8 flex flex-col relative" data-testid="plan-pro">
            <div className="absolute -top-3 left-6 px-2 py-0.5 bg-[#00E5FF] text-black text-[10px] uppercase tracking-[0.3em] font-mono font-bold">
              recommended
            </div>
            <div className="flex items-center gap-2 mb-3">
              <Crown size={14} className="text-[#00E5FF]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono">pro</span>
            </div>
            <div className="font-display font-black text-4xl mb-1">
              ${billing?.amount ?? 19}
              <span className="text-base text-zinc-400 font-mono font-normal">/month</span>
            </div>
            <div className="text-xs text-zinc-500 font-mono mb-6">cancel anytime</div>
            <ul className="space-y-2.5 text-sm text-zinc-100 mb-8 flex-1">
              <ProLine>Unlimited labs</ProLine>
              <ProLine>Unlimited AI hints</ProLine>
              <ProLine>All 5 multi-device labs</ProLine>
              <ProLine>Full progress analytics</ProLine>
              <ProLine>Priority grading</ProLine>
              <ProLine>Cancel anytime via Stripe portal</ProLine>
            </ul>
            {isPro ? (
              <div className="space-y-3">
                <div className="px-3 py-2 bg-[#00FF41]/10 border border-[#00FF41]/40 text-[#00FF41] text-xs font-mono uppercase tracking-widest text-center" data-testid="pro-active-badge">
                  ✓ Pro active
                </div>
                <button
                  onClick={openPortal}
                  disabled={loading}
                  className="w-full py-3 border border-zinc-700 hover:border-[#00E5FF] text-zinc-100 hover:text-[#00E5FF] text-sm font-mono uppercase tracking-widest transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="manage-subscription"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                  manage subscription
                </button>
              </div>
            ) : (
              <button
                onClick={startCheckout}
                disabled={loading || !billing?.enabled}
                className="w-full py-3 bg-[#00E5FF] text-black font-semibold hover:bg-[#00B8CC] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="upgrade-to-pro"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                {loading ? "redirecting…" : "upgrade to pro"}
              </button>
            )}
          </div>
        </div>

        <div className="mt-12 text-xs text-zinc-600 font-mono text-center">
          Payments secured by Stripe · Email <Link to="/" className="text-[#00E5FF]">support</Link> with any billing question
        </div>
      </main>
    </div>
  );
}

function FreeLine({ children, ok = false }) {
  return (
    <li className={`flex items-center gap-2 ${ok ? "" : "text-zinc-600 line-through"}`}>
      <Check size={14} className={ok ? "text-[#00FF41]" : "text-zinc-700"} />
      {children}
    </li>
  );
}

function ProLine({ children }) {
  return (
    <li className="flex items-center gap-2">
      <Check size={14} className="text-[#00E5FF]" />
      {children}
    </li>
  );
}
